import { Markdown } from '@kanbu/chatbot-ui';
import type { ThreadType } from '@kanbu/schema';
import { formatUtils, DateFormat } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import { cn } from '@utima/ui';

export interface ThreadContextProps {
  thread: Pick<ThreadType, 'summary' | 'workingMemory' | 'summarizedUntil'>;
  className?: string;
}

function ContextSection({
  title,
  emptyMessage,
  content,
}: {
  title: string;
  emptyMessage: string;
  content?: string | null;
}) {
  return (
    <section className='space-y-2'>
      <h4 className='text-sm font-semibold text-foreground'>{title}</h4>
      {content?.trim() ? (
        <div className='rounded-md border border-border bg-muted/30 p-3 text-sm'>
          <Markdown>{content}</Markdown>
        </div>
      ) : (
        <p className='text-sm text-muted-foreground'>{emptyMessage}</p>
      )}
    </section>
  );
}

export function ThreadContext({ thread, className }: ThreadContextProps) {
  const { t } = useLingui();

  return (
    <div className={cn('space-y-6', className)}>
      {thread.summarizedUntil ? (
        <p className='text-xs text-muted-foreground'>
          {t`Messages through ${formatUtils.date(
            thread.summarizedUntil,
            DateFormat.DateTimeNumericSeconds,
          )} are covered by the summary below.`}
        </p>
      ) : null}

      <ContextSection
        title={t`Conversation summary`}
        emptyMessage={t`No summary yet. Summaries are created when older turns fall out of the model's context window.`}
        content={thread.summary}
      />

      <ContextSection
        title={t`Working memory`}
        emptyMessage={t`Empty. The agent writes notes here when it needs facts or constraints to survive later turns.`}
        content={thread.workingMemory}
      />
    </div>
  );
}
